import React from 'react';
import {
  Alert,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import recurrenceSummary from '../utils/recurrenceSummary';

const TIMEZONE_OPTIONS = [
  'Asia/Riyadh',
  'UTC',
  'Asia/Dubai',
  'Asia/Qatar',
  'Europe/London',
];

export default function EmailCampaignScheduleStep({ value, onChange, readOnly = false, fieldErrors = {} }) {
  const sendMode = value?.send_mode || 'once';
  const recurrence = value?.recurrence || {};

  const updateValue = (patch) => {
    onChange({
      ...value,
      ...patch,
    });
  };

  const updateRecurrence = (patch) => {
    onChange({
      ...value,
      recurrence: {
        ...recurrence,
        ...patch,
      },
    });
  };

  const updateEnd = (patch) => {
    updateRecurrence({
      end: {
        ...(recurrence?.end || { type: 'never' }),
        ...patch,
      },
    });
  };

  return (
    <Stack spacing={2}>
      <FormControl>
        <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
          Send Mode
        </Typography>

        <RadioGroup
          value={sendMode}
          onChange={(event) => updateValue({ send_mode: event.target.value })}
        >
          <FormControlLabel value="now" control={<Radio />} label="Send now" disabled={readOnly} />
          <FormControlLabel value="once" control={<Radio />} label="Schedule once" disabled={readOnly} />
          <FormControlLabel value="recurring" control={<Radio />} label="Recurring" disabled={readOnly} />
        </RadioGroup>
      </FormControl>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            label="Timezone"
            value={value?.timezone || 'Asia/Riyadh'}
            onChange={(event) => updateValue({ timezone: event.target.value })}
            disabled={readOnly}
          >
            {TIMEZONE_OPTIONS.map((tz) => (
              <MenuItem key={tz} value={tz}>
                {tz}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {(sendMode === 'once' || sendMode === 'recurring') ? (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="datetime-local"
              label={sendMode === 'once' ? 'Send At' : 'First Run At'}
              InputLabelProps={{ shrink: true }}
              value={value?.send_at || ''}
              onChange={(event) => updateValue({ send_at: event.target.value })}
              disabled={readOnly}
              required={sendMode === 'once'}
              error={sendMode === 'once' && Boolean(fieldErrors?.send_at)}
              helperText={sendMode === 'once' ? fieldErrors?.send_at || '' : ''}
            />
          </Grid>
        ) : null}
      </Grid>

      {sendMode === 'recurring' ? (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Frequency"
              value={recurrence?.frequency || 'daily'}
              onChange={(event) => updateRecurrence({ frequency: event.target.value })}
              disabled={readOnly}
            >
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              label="Interval"
              value={recurrence?.interval || 1}
              onChange={(event) => updateRecurrence({ interval: Number(event.target.value || 1) })}
              inputProps={{ min: 1 }}
              disabled={readOnly}
            />
          </Grid>

          {recurrence?.frequency === 'weekly' ? (
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Weekdays (1-7, comma separated)"
                value={Array.isArray(recurrence?.weekdays) ? recurrence.weekdays.join(',') : ''}
                onChange={(event) => {
                  const weekdays = String(event.target.value || '')
                    .split(',')
                    .map((item) => Number(item.trim()))
                    .filter((item) => item >= 1 && item <= 7);
                  updateRecurrence({ weekdays });
                }}
                disabled={readOnly}
              />
            </Grid>
          ) : null}

          {recurrence?.frequency === 'monthly' ? (
            <>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Monthly Mode"
                  value={recurrence?.mode || 'day_of_month'}
                  onChange={(event) => updateRecurrence({ mode: event.target.value })}
                  disabled={readOnly}
                >
                  <MenuItem value="day_of_month">Day of month</MenuItem>
                  <MenuItem value="nth_weekday">Nth weekday</MenuItem>
                </TextField>
              </Grid>

              {(recurrence?.mode || 'day_of_month') === 'day_of_month' ? (
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Day"
                    value={recurrence?.day || 1}
                    onChange={(event) => updateRecurrence({ day: Number(event.target.value || 1) })}
                    inputProps={{ min: 1, max: 31 }}
                    disabled={readOnly}
                  />
                </Grid>
              ) : (
                <>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Weekday (1-7)"
                      value={recurrence?.weekday || 1}
                      onChange={(event) => updateRecurrence({ weekday: Number(event.target.value || 1) })}
                      inputProps={{ min: 1, max: 7 }}
                      disabled={readOnly}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Nth"
                      value={recurrence?.nth || 1}
                      onChange={(event) => updateRecurrence({ nth: Number(event.target.value || 1) })}
                      inputProps={{ min: 1, max: 5 }}
                      disabled={readOnly}
                    />
                  </Grid>
                </>
              )}
            </>
          ) : null}

          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="End Condition"
              value={recurrence?.end?.type || 'never'}
              onChange={(event) => updateEnd({ type: event.target.value })}
              disabled={readOnly}
            >
              <MenuItem value="never">Never</MenuItem>
              <MenuItem value="end_date">End date</MenuItem>
              <MenuItem value="after_occurrences">After occurrences</MenuItem>
            </TextField>
          </Grid>

          {recurrence?.end?.type === 'end_date' ? (
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                InputLabelProps={{ shrink: true }}
                value={recurrence?.end?.end_date || ''}
                onChange={(event) => updateEnd({ end_date: event.target.value })}
                disabled={readOnly}
              />
            </Grid>
          ) : null}

          {recurrence?.end?.type === 'after_occurrences' ? (
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Occurrences"
                value={recurrence?.end?.occurrences || 1}
                onChange={(event) => updateEnd({ occurrences: Number(event.target.value || 1) })}
                inputProps={{ min: 1 }}
                disabled={readOnly}
              />
            </Grid>
          ) : null}
        </Grid>
      ) : null}

      <Alert severity="info">{recurrenceSummary(value)}</Alert>
    </Stack>
  );
}
